import {
  bindService,
  createServiceToken,
  type AppFeatureDefinition,
  type ServiceBinding,
} from "@nasam/kernel";
import {
  assetsRepositoryToken,
  companiesRepositoryToken,
  type AssetsRepository,
  type CompaniesRepository,
} from "../../contracts";
import { createDevicesHttpRepository } from "./data";
import type { DevicesRepository } from "./domain";
import { DEVICES_VIEW, DEVICE_TYPES_VIEW } from "./devicesPermissions";
import { createDevicesRoutes } from "./presentation/routes";

export * from "./devicesPermissions";

export const devicesRepositoryToken =
  createServiceToken<DevicesRepository>("devices.repository");

function createDevicesServiceBindings(
  httpClient: Parameters<
    NonNullable<AppFeatureDefinition["provideServices"]>
  >[0]["httpClient"],
): readonly ServiceBinding[] {
  return [
    bindService(
      devicesRepositoryToken,
      createDevicesHttpRepository(httpClient),
    ),
  ];
}

export const devicesFeature: AppFeatureDefinition = {
  id: "devices",
  version: "0.1.0",
  titleKey: "devices:moduleTitle",
  icon: "Cpu",
  basePath: "/devices",
  requiredPermissions: [DEVICE_TYPES_VIEW, DEVICES_VIEW],
  // Every CORE feature shares one "core" rail node (see mergeFeatureNavNodes);
  // this feature contributes the "Devices" drawer group under it.
  navigation: [
    {
      id: "core",
      titleKey: "common:businessModules.core",
      icon: "Layers",
      placement: "rail",
      order: 10,
      children: [
        {
          id: "devices",
          titleKey: "devices:moduleTitle",
          icon: "Cpu",
          placement: "drawer",
          order: 50,
          requiredPermissions: [DEVICES_VIEW],
          children: [
            {
              // Wording ("Device Assignment" / "تعيين الأجهزة") is kept as
              // sensorAssignments' own moduleTitle key -- it was already
              // tuned to match the design's label. Moved here (was under
              // assetsFeature's "assets" group) now that this tab is shown.
              // devices-types (DeviceTypesListScreen) stays out for now --
              // still an unbuilt ModulePlaceholderScreen.
              id: "device-assignment",
              titleKey: "sensorAssignments:moduleTitle",
              icon: "Radio",
              placement: "drawer",
              order: 10,
              path: "/devices",
              requiredPermissions: [DEVICES_VIEW],
            },
          ],
        },
      ],
    },
  ],
  routes: (context) =>
    createDevicesRoutes({
      context,
      devices: context.services.resolve(devicesRepositoryToken),
      companies: context.services.tryResolve<CompaniesRepository>(
        companiesRepositoryToken,
      ),
      assets: context.services.tryResolve<AssetsRepository>(
        assetsRepositoryToken,
      ),
    }),
  permissionCatalog: [
    {
      segment: "core",
      labelKey: "devices:permissions.core",
      children: [
        {
          segment: "device_management",
          labelKey: "devices:permissions.deviceManagement",
          children: [
            {
              segment: "device_type_management_list",
              labelKey: "devices:permissions.deviceTypes",
              actions: [
                { id: "view", labelKey: "devices:actions.view" },
                { id: "create", labelKey: "devices:actions.create" },
                { id: "update", labelKey: "devices:actions.update" },
                { id: "delete", labelKey: "devices:actions.delete" },
              ],
            },
            {
              segment: "device_management_list",
              labelKey: "devices:permissions.devices",
              actions: [
                { id: "view", labelKey: "devices:actions.view" },
                { id: "create", labelKey: "devices:actions.create" },
                { id: "update", labelKey: "devices:actions.update" },
                { id: "delete", labelKey: "devices:actions.delete" },
              ],
            },
            {
              // Irregular screen segment: `sensor`, not `sensor_list`. The Sensors
              // Management screen itself lives in sensorAssignmentsFeature; the
              // catalog entry stays here since the segment belongs to the
              // backend's device_management tree.
              segment: "sensor",
              labelKey: "devices:permissions.sensors",
              actions: [
                { id: "create", labelKey: "devices:actions.create" },
                { id: "view", labelKey: "devices:actions.view" },
              ],
            },
          ],
        },
      ],
    },
  ],
  provideServices: ({ httpClient }) => createDevicesServiceBindings(httpClient),
};
